
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import * as echarts from 'echarts';
import StackedBarChartFilterable from '@/components/reports-v3/components/filterables/StackedBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import { GobiColor } from '@/helpers/color';
import gdbx from '@/store/modules/gdbx';
import { DataViewFormatter } from '../../elements/charts/helpers/dataViewFormatter';
import { TooltipFormatter } from '../../elements/charts/helpers/tooltipFormatter';
import { getTableName } from '@/worker/db/sqlHelpers';

@Component({
	components: {
		StackedBarChartFilterable,
	},
})
export default class SupplierDocumentAnalysisInvoiceVsPaymentComparisonBarChart extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['suppliers'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['suppliers', 'agents', 'dateRange'];

	public amount: number[] = [0, 0, 0, 0, 0];
	public result: any = [];
	public xAxisData: string[] = [];
	public series: echarts.ECharts[] | any = [];

	public get dateFormatted() {
		return this.selectedDateRange;
	}

	public get currency() {
		return gdbx.currencySymbol;
	}

	public get dataViewFormatter() {
		return DataViewFormatter.barChartWithPointShare;
	}

	public get tooltipFormatter() {
		return TooltipFormatter.basicWithPointShare;
	}

	public get expensiveHook() {
		const { selectedDateRange, selectedAgents, selectedSuppliers } = this;
		return JSON.stringify([
			selectedDateRange,
			selectedAgents,
			selectedSuppliers,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.4);

		const queryHead = await this._ensureTablesLoaded(
			['supplierTrades'] as any[],
		);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.4, 0.9);

		const tradesTable = getTableName('supplierTrades');
		const dateFilter = `TRY_CAST("DOCDATE" AS BIGINT) BETWEEN ${this.selectedDateRange[0]} AND ${this.selectedDateRange[1]}`;
		const escapeStr = (s: string) => s.replace(/'/g, '\'\'');

		const allAgentLen = (gdbx.allAgents && gdbx.allAgents.length) || 0;
		const agentFilter = this.selectedAgents && this.selectedAgents.length > 0
			&& this.selectedAgents.length !== allAgentLen
			? ` AND "AGENT" IN (${this.selectedAgents.map((a: string) => `'${escapeStr(a)}'`).join(', ')})`
			: '';

		const allSupplierLen = (gdbx.allSuppliers && gdbx.allSuppliers.length) || 0;
		const supplierFilter = this.selectedSuppliers && this.selectedSuppliers.length > 0
			&& this.selectedSuppliers.length !== allSupplierLen
			? ` AND "CODE" IN (${this.selectedSuppliers.map((s: string) => `'${escapeStr(s)}'`).join(', ')})`
			: '';

		// Per doc type: the filtered amount (selected suppliers/agents) and the all-suppliers/agents
		// baseline (same date range, no supplier/agent filter). % = filtered / baseline (contribution).
		const supplierAgentCond = `1=1${agentFilter}${supplierFilter}`;
		const data = await queryHead.rawQuery(`
			SELECT
				SUM(CASE WHEN "DOCTYPE" = 'PI' AND ${supplierAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as pi,
				SUM(CASE WHEN "DOCTYPE" = 'SD' AND ${supplierAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sd,
				SUM(CASE WHEN "DOCTYPE" = 'SP' AND ${supplierAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sp,
				SUM(CASE WHEN "DOCTYPE" = 'SC' AND ${supplierAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sc,
				SUM(CASE WHEN "DOCTYPE" = 'ST' AND ${supplierAgentCond} THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as st,
				SUM(CASE WHEN "DOCTYPE" = 'PI' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as pi_total,
				SUM(CASE WHEN "DOCTYPE" = 'SD' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sd_total,
				SUM(CASE WHEN "DOCTYPE" = 'SP' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sp_total,
				SUM(CASE WHEN "DOCTYPE" = 'SC' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as sc_total,
				SUM(CASE WHEN "DOCTYPE" = 'ST' THEN TRY_CAST("LOCALAMOUNT" AS DOUBLE) ELSE 0 END) as st_total
			FROM ${tradesTable}
			WHERE ${dateFilter}
				AND "DOCTYPE" IN ('PI', 'SD', 'SP', 'SC', 'ST')
		`);
		this.checkInterrupted(jobId);
		this.generateLoadingText(1);

		const row = data[0] || {};

		// Reversed order to match original: ST, SC, SP, SD, PI
		const codeList: string[] = ['PI', 'SD', 'SP', 'SC', 'ST'].reverse();
		const nameList: string[] = [
			'Invoice',
			'Debit Note',
			'Payment',
			'Credit Note',
			'Contra',
		].reverse();

		const codeToAlias: { [key: string]: string } = {
			PI: 'pi', SD: 'sd', SP: 'sp', SC: 'sc', ST: 'st',
		};

		this.amount = codeList.map((code) => Number(row[codeToAlias[code]]) || 0);
		const baseline = codeList.map((code) => Number(row[codeToAlias[code] + '_total']) || 0);
		this.xAxisData = nameList;

		this.result = [];

		this.amount.forEach((item, index) => {
			const total = baseline[index];
			const percent = total > 0 ? (item / total) * 100 : 0;
			this.result.push({
				value: item,
				itemStyle: { color: index > 2 ? GobiColor.COLOR1 : GobiColor.COLOR3 },
				percent,
			});
		});

		this.series = [
			{
				name: 'Amount',
				data: this.result,
				type: 'bar',
				barWidth: '40%',
			},
		];

		this.saveHistory('series', 'xAxisData', 'result', 'amount');
	}
}
